import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ASYNC_TASK_NAMES } from "@performyard/models";
import { Text, TextDate } from "@performyard/py-components";
import {
	MetaDataSurveyInsightResponseStream,
	SurveyInsightResponseStreamJson,
	UserErrorResponseJson,
} from "@performyard/service-contracts";

import { useSurvey } from "../../../pages/Surveys/pages/SurveyDetails/hooks/useSurvey";

import { useCompanyAsyncTask } from "../../../hooks/useCompanyAsyncTask";

import { postQueryAi } from "../../../api/services/defs/internal-api";
import { getActiveCompanyTasksByName } from "../../../api/services/defs/internal-api/async-tasks";

import { InsightsGeneratedAt } from "../InsightsGeneratedAt";
import { AISidePanel } from "../SidePanel";

type SurveyInsightModuleProps = {
	surveyId: string;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
};

type ComponentPhase =
	| "idle"
	| "checking_cache"
	| "creating_task"
	| "streaming"
	| "fetching_result"
	| "complete"
	| "error";

const USER_FRIENDLY_ERROR = "The generation of Survey Insights was unsuccessful. Please try again.";
const MAX_FETCH_ATTEMPTS = 5;
const FETCH_RETRY_DELAY_MS = 2000;

export const SurveyInsightModule = ({ surveyId, isOpen, onOpenChange }: SurveyInsightModuleProps) => {
	const [phase, setPhase] = useState<ComponentPhase>("idle");
	const [cachedInsight, setCachedInsight] = useState<MetaDataSurveyInsightResponseStream | null>(null);
	const [initialTaskData, setInitialTaskData] = useState<MetaDataSurveyInsightResponseStream | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const { survey } = useSurvey({ surveyId });

	// Track the current operation to prevent stale callbacks
	const operationIdRef = useRef(0);
	// Track fetch retry attempts to prevent infinite loops
	const fetchAttemptRef = useRef(0);

	const { task, triggerTask, error: taskError } = useCompanyAsyncTask(ASYNC_TASK_NAMES.GENERATE_SURVEY_INSIGHTS);

	/**
	 * Creates a new insight generation task
	 * @param isRetry - Whether this is a retry/regenerate request
	 */
	const createTask = useCallback(
		async (isRetry: boolean): Promise<void> => {
			const currentOperationId = ++operationIdRef.current;
			fetchAttemptRef.current = 0;

			setPhase("creating_task");
			setCachedInsight(null);
			setInitialTaskData(null);
			setErrorMessage(null);

			try {
				await triggerTask(async () => {
					const response = await postQueryAi<MetaDataSurveyInsightResponseStream | UserErrorResponseJson>({
						type: "survey-insight",
						surveyId,
						findAdditionalContext: false,
						retry: isRetry ? "true" : "false",
						responseStyle: "structured",
					});

					// Check if this operation is still current
					if (currentOperationId !== operationIdRef.current) {
						throw new Error("Operation cancelled");
					}

					if ("message" in response) {
						throw new Error(response.message);
					}

					if (!response.taskId) {
						// Single-chunk survey — completed synchronously
						if (response.status === "completed") {
							setCachedInsight(response);
							setPhase("complete");
							return { taskId: "" };
						}
						throw new Error("No taskId returned from AI service");
					}

					// Store the initial response data for progress display
					if (response.status === "in_progress") {
						setInitialTaskData(response);
					}

					setPhase("streaming");
					return { taskId: response.taskId };
				});
			} catch (error) {
				// Only update state if this is still the current operation
				if (currentOperationId === operationIdRef.current) {
					const message = error instanceof Error ? error.message : "Failed to create insight task";
					if (message !== "Operation cancelled") {
						console.error("Error creating insight task:", message);
						setErrorMessage(USER_FRIENDLY_ERROR);
						setPhase("error");
					}
				}
				throw error;
			}
		},
		[surveyId, triggerTask]
	);

	/**
	 * Fetches the completed insight from the API
	 */
	const fetchCompletedInsight = useCallback(async (): Promise<void> => {
		const currentOperationId = operationIdRef.current;

		setPhase("fetching_result");

		try {
			const response = await postQueryAi<MetaDataSurveyInsightResponseStream | UserErrorResponseJson>({
				type: "survey-insight",
				surveyId,
				findAdditionalContext: false,
				retry: "false",
				responseStyle: "structured",
			});

			// Check if this operation is still current
			if (currentOperationId !== operationIdRef.current) {
				return;
			}

			if ("message" in response) {
				console.error("Error fetching insight:", response.message);
				setErrorMessage(USER_FRIENDLY_ERROR);
				setPhase("error");
				return;
			}

			// Check if this is a valid completed insight
			const hasProcessingState =
				response.data && typeof response.data === "object" && "processing" in response.data;

			if (response.isCached || response.status === "completed" || !hasProcessingState) {
				fetchAttemptRef.current = 0;
				setCachedInsight({
					...response,
					status: "completed" as const,
				});
				setPhase("complete");
			} else {
				// Still processing — retry with a cap
				fetchAttemptRef.current++;
				if (fetchAttemptRef.current >= MAX_FETCH_ATTEMPTS) {
					console.error("Max fetch attempts reached for insight result");
					setErrorMessage(USER_FRIENDLY_ERROR);
					setPhase("error");
					return;
				}
				// Delay before retrying to avoid tight loops
				setTimeout(() => {
					if (currentOperationId === operationIdRef.current) {
						setPhase("streaming");
					}
				}, FETCH_RETRY_DELAY_MS);
			}
		} catch (error) {
			if (currentOperationId === operationIdRef.current) {
				console.error("Error fetching insight:", error);
				setErrorMessage(USER_FRIENDLY_ERROR);
				setPhase("error");
			}
		}
	}, [surveyId]);

	/**
	 * Check for existing insight or active task when panel opens
	 */
	useEffect(() => {
		if (!isOpen) {
			// Bump operationId to invalidate any in-flight async operations
			operationIdRef.current++;
			return;
		}

		const checkInsightStatus = async (): Promise<void> => {
			const currentOperationId = ++operationIdRef.current;
			fetchAttemptRef.current = 0;

			setPhase("checking_cache");
			setErrorMessage(null);

			try {
				// Step 1: Check for active tasks for this survey
				const activeTasks = await getActiveCompanyTasksByName(ASYNC_TASK_NAMES.GENERATE_SURVEY_INSIGHTS);

				if (currentOperationId !== operationIdRef.current) {
					return;
				}

				// Find a task matching this surveyId
				const matchingTask = activeTasks.find((t) => {
					const metadata = t.metadata as { surveyId?: string } | undefined;
					return metadata?.surveyId === surveyId;
				});

				if (matchingTask) {
					// Active task found - connect to SSE
					setPhase("streaming");
					await triggerTask(() => Promise.resolve({ taskId: matchingTask._id }));
					return;
				}

				// Step 2: No active task - check for cached/completed insight
				const response = await postQueryAi<MetaDataSurveyInsightResponseStream | UserErrorResponseJson>({
					type: "survey-insight",
					surveyId,
					findAdditionalContext: false,
					retry: "false",
					responseStyle: "structured",
				});

				if (currentOperationId !== operationIdRef.current) {
					return;
				}

				// Handle error response - no cached insight exists
				if ("message" in response) {
					await createTask(false);
					return;
				}

				// Completed insight exists
				if (response.status === "completed") {
					setCachedInsight(response);
					setPhase("complete");
					return;
				}

				// In progress with taskId - reconnect SSE
				if (response.status === "in_progress" && response.taskId) {
					setInitialTaskData(response);
					setPhase("streaming");
					await triggerTask(() => Promise.resolve({ taskId: response.taskId! }));
					return;
				}

				// No completed insight and no active task - create new one
				await createTask(false);
			} catch (error) {
				if (currentOperationId === operationIdRef.current) {
					console.error("Error checking insight status:", error);
					setErrorMessage(USER_FRIENDLY_ERROR);
					setPhase("error");
				}
			}
		};

		checkInsightStatus();
	}, [isOpen, surveyId, createTask, triggerTask]);

	/**
	 * Handle task status changes from SSE
	 */
	const taskStatus = task?.status;
	useEffect(() => {
		if (taskStatus === "completed" && phase === "streaming") {
			fetchCompletedInsight();
		} else if (taskStatus === "failed" && phase === "streaming") {
			console.error("Task failed:", task?.error);
			setErrorMessage(USER_FRIENDLY_ERROR);
			setPhase("error");
		} else if ((taskStatus === "processing" || taskStatus === "queued") && phase === "creating_task") {
			setPhase("streaming");
		}
	}, [taskStatus, phase, task?.error, fetchCompletedInsight]);

	/**
	 * Handle retry/regenerate button click
	 */
	const handleRetry = useCallback(async (): Promise<void> => {
		await createTask(true);
	}, [createTask]);

	/**
	 * Construct display data from task or cached insight
	 */
	const displayData = useMemo<MetaDataSurveyInsightResponseStream | null>(() => {
		// Show cached/completed insight
		if (cachedInsight && phase === "complete") {
			return cachedInsight;
		}

		// Show progress from SSE task
		if (task && (task.status === "processing" || task.status === "queued" || task.status === "completed")) {
			const progressData = {
				processing: true as const,
				totalChunks: task.progressTotal || 1,
				completedChunks: task.status === "completed" ? task.progressTotal || 1 : task.progressCount,
			};
			return {
				status: "in_progress" as const,
				data: progressData as unknown as MetaDataSurveyInsightResponseStream["data"],
				promptHash: "",
				isCached: false,
				createdAt: task.createdAt,
				userId: initialTaskData?.userId,
				createdBy: initialTaskData?.createdBy,
			};
		}

		// Fall back to initial task data (before SSE updates arrive)
		if (initialTaskData && initialTaskData.status === "in_progress") {
			return initialTaskData;
		}

		return null;
	}, [task, cachedInsight, initialTaskData, phase]);

	/**
	 * Determine if we should show loading state
	 */
	const isLoading = useMemo((): boolean => {
		switch (phase) {
			case "checking_cache":
			case "creating_task":
			case "streaming":
			case "fetching_result":
				return true;
			case "complete":
			case "error":
			case "idle":
				return false;
			default:
				return false;
		}
	}, [phase]);

	/**
	 * Determine if we should show the donut chart (chunking progress)
	 */
	const showDonutProgress = useMemo((): boolean => {
		if ((phase === "streaming" || phase === "fetching_result") && displayData) {
			return true;
		}
		return false;
	}, [phase, displayData]);

	/**
	 * Compute the error to display
	 */
	const displayError = useMemo((): UserErrorResponseJson | null => {
		if (errorMessage) {
			return { message: errorMessage, details: [] };
		}

		if (task?.status === "failed") {
			console.error("Task error:", task.error);
			return {
				message: USER_FRIENDLY_ERROR,
				details: [],
			};
		}

		if (taskError) {
			console.error("Task hook error:", taskError.message);
			return { message: USER_FRIENDLY_ERROR, details: [] };
		}

		return null;
	}, [errorMessage, task?.status, task?.error, taskError]);

	const formatSurveyDateString = () => {
		if (survey?.startDate && survey?.endDate) {
			return (
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					<span className="flex flex-row items-center gap-1">
						Start and End Date:
						<span className="flex flex-row items-center">
							<TextDate
								date={new Date(survey.startDate)}
								dateSize="medium"
								hasTime={false}
								size="paragraph-3"
								weight="regular"
								color="neutral-9"
								overrideElement="span"
							/>
							-
							<TextDate
								date={new Date(survey.endDate)}
								dateSize="medium"
								hasTime={false}
								size="paragraph-3"
								weight="regular"
								color="neutral-9"
								overrideElement="span"
							/>
						</span>
					</span>
				</Text>
			);
		} else if (survey?.startDate) {
			return (
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					<span className="flex flex-row items-center gap-1">
						Start Date:
						<TextDate
							date={new Date(survey.startDate)}
							dateSize="medium"
							hasTime={false}
							overrideElement="span"
							color="neutral-9"
							weight="regular"
							size="paragraph-3"
						></TextDate>
					</span>
				</Text>
			);
		}
		return null;
	};

	const renderHeader = (data: SurveyInsightResponseStreamJson, isChunking: boolean) => (
		<div className="border-radius-1 flex w-full flex-col items-center justify-center gap-4 self-stretch border-b border-solid border-neutral-200 pb-4">
			<div className="flex items-start gap-5 self-stretch rounded bg-neutral-100 p-2">
				<div className="flex flex-1 shrink-0 basis-0 flex-col items-start gap-2">
					<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
						{isChunking ? "Survey Insights" : data.title}
					</Text>
					{!isChunking && formatSurveyDateString()}
				</div>
			</div>
		</div>
	);

	const renderInfoSection = (data: SurveyInsightResponseStreamJson): React.ReactNode => (
		<>
			<InsightsGeneratedAt label="Insights Populated" date={data.createdAt} generatedBy={data.createdBy} />
			{survey?.status !== "closed" && (
				<Text size="caption" weight="regular" color="neutral-9">
					**Generated from incomplete survey results
				</Text>
			)}
		</>
	);

	return (
		<AISidePanel<MetaDataSurveyInsightResponseStream>
			type="survey"
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			data={displayData && !displayError ? displayData : null}
			isLoading={isLoading && !showDonutProgress}
			renderHeader={renderHeader}
			renderInfoSection={renderInfoSection}
			onRetry={handleRetry}
			error={displayError}
			logoLoaderProps={{
				textArray: [
					"Retrieving Survey Participants…",
					"Compiling Responses…",
					"Organizing Responses…",
					"Processing Responses…",
					"Evaluating Summarization…",
					"Structuring Summary Format…",
					"Finalizing Survey Results…",
					"Finishing Up…",
				],
				mainText: "Processing Survey Insights",
			}}
		/>
	);
};
